import {CardNumberElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useCreateStripeSetupIntent} from "@/hooks/useCreateStripeSetupIntent";

interface BillingDetails {
    name: string;
    postalCode: string;
}

/**
 * Confirm a card via a SetupIntent using the mounted Stripe Elements. Mints a
 * SetupIntent, then confirms it with the CardNumberElement — the card PAN/CVC
 * stay inside Stripe's iframes and never touch our code. AVS/CVC + Radar + any
 * 3DS challenge run here. Returns the verified paymentMethodId, or throws an
 * ApiError-shaped { message } carrying Stripe's reason for the caller to surface.
 *
 * MUST be called from a component rendered inside an <Elements> provider.
 */
export function useConfirmCardSetup() {
    const stripe = useStripe();
    const elements = useElements();
    const {mutateAsync: createSetupIntent} = useCreateStripeSetupIntent();

    return async function confirmCardSetup(billingDetails: BillingDetails): Promise<string> {
        if (!stripe || !elements) {
            throw {message: "The payment form isn't ready yet. Please try again."};
        }

        const {clientSecret} = await createSetupIntent();
        const {error, setupIntent} = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: elements.getElement(CardNumberElement),
                billing_details: {
                    name: billingDetails.name.trim(),
                    address: {
                        postal_code: billingDetails.postalCode,
                        country: "US",
                    },
                },
            },
        });

        if (error) {
            throw {message: error.message};
        }

        return setupIntent?.payment_method as string;
    };
}
